import {useState, useRef, useEffect, useCallback} from 'react'
import QrScanner from 'qr-scanner'
import {getCameraErrorMessage} from 'shared/utils/cameraUtils'

const useQrScanner = () => {
	const [isInitializing, setIsInitializing] = useState(true)
	const [scannedData, setScannedData] = useState('')
	const [error, setError] = useState('')
	const [hasScannedData, setHasScannedData] = useState(false)
	const videoRef = useRef(null)
	const qrScannerRef = useRef(null)
	const checkCameraPermission = useCallback(async () => {
		try {
			// Check if camera permission is available
			const hasCamera = await QrScanner.hasCamera()
			if (!hasCamera) {
				throw new Error('NO_CAMERA')
			}

			// Request camera permission
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {facingMode: 'environment'},
			})
			// Stop the stream immediately as we just wanted to check permission
			stream.getTracks().forEach((track) => track.stop())
			return true
		} catch (error) {
			console.error('Camera permission check failed:', error)
			if (error.name === 'NotAllowedError') {
				throw new Error('PERMISSION_DENIED')
			} else if (
				error.name === 'NotFoundError' ||
				error.message === 'NO_CAMERA'
			) {
				throw new Error('NO_CAMERA')
			} else if (error.name === 'NotSupportedError') {
				throw new Error('NOT_SUPPORTED')
			}
			throw new Error('UNKNOWN')
		}
	}, [])

	const stopScanning = useCallback(() => {
		if (qrScannerRef.current) {
			qrScannerRef.current.stop()
			qrScannerRef.current.destroy()
			qrScannerRef.current = null
		}
		setIsInitializing(false)
	}, [])

	const startScanning = useCallback(async () => {
		try {
			setError('')
			setIsInitializing(true)

			if (!videoRef.current) return

			// Check camera permission first
			await checkCameraPermission()

			qrScannerRef.current = new QrScanner(
				videoRef.current,
				(result) => {
					setScannedData(result.data)
					setHasScannedData(true)
					// Stop scanning after successful detection to prevent multiple calls
					stopScanning()
				},
				{
					onDecodeError: (error) => {
						// This is expected when no QR code is in view
						console.log('No QR code found:', error)
					},
					highlightScanRegion: true,
					highlightCodeOutline: true,
					preferredCamera: 'environment',
				}
			)

			await qrScannerRef.current.start()
			setIsInitializing(false)
		} catch (error) {
			console.error('Error starting QR scanner:', error)
			const errorMessage = getCameraErrorMessage(error)
			setError(errorMessage)
			setIsInitializing(false)
		}
	}, [checkCameraPermission, stopScanning])

	const clearAndRescan = useCallback(() => {
		setScannedData('')
		setHasScannedData(false)
		setError('')
		startScanning()
	}, [startScanning])

	// Auto-start scanning when component mounts
	useEffect(() => {
		startScanning()

		return () => {
			stopScanning()
		}
	}, [startScanning, stopScanning])

	return {
		// State
		isInitializing,
		scannedData,
		error,
		hasScannedData,
		// Refs
		videoRef,
		// Actions
		startScanning,
		stopScanning,
		clearAndRescan,
	}
}

export default useQrScanner
